import { useEffect, useMemo, useState } from 'react';
import { Drawer, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useStores } from 'Hooks/useStore';
import useLanguageNames from 'LearningManager/hooks/useLanguageNames';
import styles from './ViewDetailsDrawer.module.scss';
import { Icon } from 'Components';
import { Assignment } from 'LearningManager/constants/CoursesAssignment.types';
import { AssignmentSource } from 'Services/Api/AssignmentSources/Types';
import AssignmentBadge from 'LearningManager/components/AssignmentBadge';
import { certificationLabel } from 'LearningManager/helpers/assignment';
import { formatCardDate, formatRunTime } from 'LearningManager/helpers/date';
import { lmsAxiosClient } from 'LearningManager/service/lmsClient';
import ACTIVITIES_API from 'Services/Api/Activities/Api';

interface ViewDetailsDrawerProps {
	visible: boolean;
	assignment: Assignment | null;
	source?: AssignmentSource;
	onClose: () => void;
}

interface TrainingProgress {
	id: number;
	completed: number;
	in_progress: number;
	not_started: number;
	total: number;
}

interface ContentItem {
	key: string;
	label: string;
}

const NOT_AVAILABLE = '-';

const contentItems = (assignment: Assignment | null): ContentItem[] => {
	const learningPaths = assignment?.learningPaths ?? [];
	const learningPathCourseIds = new Set(
		learningPaths.flatMap((lp) => lp.courses.map((course) => course.id))
	);

	return [
		...learningPaths.map((lp) => ({ key: lp.id, label: lp.label })),
		...(assignment?.individualCourses ?? [])
			.filter((course) => !learningPathCourseIds.has(course.id))
			.map((course) => ({
				key: `course-${course.id}`,
				label: course.title
			}))
	];
};

type ProgressKey = 'completed' | 'inProgress' | 'notStarted';

const PROGRESS_SEGMENTS: { key: ProgressKey; label: string }[] = [
	{ key: 'completed', label: 'learning.courseAssignments.filters.completed' },
	{ key: 'inProgress', label: 'learning.courseAssignments.filters.inProgress' },
	{ key: 'notStarted', label: 'learning.courseAssignments.filters.notStarted' }
];

type ProgressBreakdown = Record<
	ProgressKey,
	{ count: number | null; width: number }
>;

const progressBreakdown = (
	userProgress: TrainingProgress | null,
	completionPercentage: number
): ProgressBreakdown => {
	const { completed = 0, in_progress = 0, not_started = 0 } = userProgress ?? {};
	const total = completed + in_progress + not_started;
	if (total > 0) {
		return {
			completed: {
				count: completed,
				width: (completed / total) * 100
			},
			inProgress: {
				count: in_progress,
				width: (in_progress / total) * 100
			},
			notStarted: {
				count: not_started,
				width: (not_started / total) * 100
			}
		};
	}

	return {
		completed: {
			count: completed ?? null,
			width: Math.min(Math.max(completionPercentage, 0), 100)
		},
		inProgress: { count: in_progress ?? null, width: 0 },
		notStarted: { count: not_started ?? null, width: 0 }
	};
};

const assignmentCourseIds = (assignment: Assignment | null): number[] => [
	...(assignment?.learningPaths ?? []).flatMap((lp) =>
		lp.courses.map((course) => course.id)
	),
	...(assignment?.individualCourses ?? []).map((course) => course.id)
];

const ViewDetailsDrawer: React.FC<ViewDetailsDrawerProps> = observer(
	({ visible, assignment, source, onClose }) => {
		const [progressData, setProgressData] = useState<TrainingProgress | null>(null);
		const [loading, setLoading] = useState(false);
		const { t } = useTranslation();
		const { learningManagerStore } = useStores();
		const getLanguageNames = useLanguageNames();
		const assignmentId = Number(assignment?.id ?? '0');

		const assignementData = learningManagerStore.assignmentSources.find(source => source.id === assignmentId);
		const languages = getLanguageNames(assignmentCourseIds(assignment).flatMap((id) => learningManagerStore.coursesById[id]?.language_ids ?? []));
		const content = contentItems(assignment);
		const scopes = assignementData?.scopes ?? [];

		const progress = useMemo(() => {
			return progressBreakdown(progressData, assignment?.completionPercentage ?? 0);
		}, [progressData]);

		useEffect(() => {
			if (!assignmentId) return;
			(async () => {
				try {
					setLoading(true);
					const { data } = await lmsAxiosClient.get(`${ACTIVITIES_API.ASSIGNMENT_DETAILS}/${assignmentId}/detail`);
					setProgressData(data?.data || null);
				} catch (error) {
					setProgressData(null);
				} finally {
					setLoading(false);
				}
			})()
		}, [assignmentId]);

		return (
			<Drawer
				className={styles.assignmentDetailsDrawer}
				placement="right"
				visible={visible}
				width={420}
				onClose={() => {
					setProgressData(null);
					onClose();
				}}
				title={
					<div className={styles.drawerTitle}>
						<div className={styles.drawerTitle__name}>
							{assignment?.name ?? ''}
						</div>
						<div className={styles.drawerTitle__badges}>
							{assignment?.type && (
								<AssignmentBadge type={assignment.type} />
							)}
							{assignment?.status && (
								<AssignmentBadge type={assignment.status} />
							)}
						</div>
					</div>
				}
			>
				<div className={styles.drawerSection}>
					<div className={styles.drawerSection__heading}>
						{t('learning.viewDetails.details')}
					</div>
					<div className={styles.detailLine}>
						{t('learning.viewDetails.assignmentId')}:{' '}
						{assignment?.id ? `#${assignment?.id}` : NOT_AVAILABLE}
					</div>
					<div className={styles.detailLine}>
						{t('learning.viewDetails.created')}:{' '}
						{assignementData?.created_at ? formatCardDate(assignementData?.created_at) : NOT_AVAILABLE}
					</div>
					<div className={styles.detailLine}>
						{t('learning.viewDetails.updated')}:{' '}
						{assignementData?.updated_date ? formatCardDate(assignementData?.updated_date) : NOT_AVAILABLE}
					</div>
					<div className={styles.detailLine}>
						{t('learning.viewDetails.start')}:{' '}
						{assignment?.startsDate ? formatCardDate(assignment?.startsDate) : NOT_AVAILABLE}
					</div>
					<div className={styles.detailLine}>
						{t('learning.viewDetails.duration')}:{' '}
						{assignementData?.run_time ? formatRunTime(assignementData?.run_time) : NOT_AVAILABLE}
					</div>
				</div>

				<div className={styles.drawerSection}>
					<div className={styles.progressHeader}>
						<div className={styles.drawerSection__heading}>
							{t('learning.viewDetails.progressOverview')}
						</div>
						<Tooltip
							title={t('learning.viewDetails.progressTooltip')}
							placement="bottomRight"
							arrowPointAtCenter
							overlayClassName={styles.progressTooltip}
						>
							<Icon
								type="infoOutlined"
								role="img"
								className={styles.progressInfoIcon}
							/>
						</Tooltip>
					</div>

					<div className={styles.progressTrack}>
						{loading ?
							<div className={classNames(styles.progressFill, styles[`progressFill--loading`])} />
							: PROGRESS_SEGMENTS.map(({ key }) =>
								progress[key].width > 0 ? (
									<div
										key={key}
										className={classNames(
											styles.progressFill,
											styles[`progressFill--${key}`]
										)}
										style={{
											width: `${progress[key].width}%`
										}}
									/>
								) : null
							)}
					</div>

					<div className={styles.progressLegend}>
						{PROGRESS_SEGMENTS.map(({ key, label }) => (
							<div
								key={key}
								className={styles.progressLegend__item}
							>
								<span
									className={classNames(
										styles.progressDot,
										styles[`progressDot--${key}`]
									)}
								/>
								{t(label)}: {progress[key].count ?? NOT_AVAILABLE}
							</div>
						))}
					</div>
				</div>

				<div className={styles.drawerSection}>
					<div className={styles.drawerSection__heading}>
						{t('learning.viewDetails.content')}
					</div>
					{content.length ? (
						content.map((item, index) => (
							<div key={item.key} className={styles.listLine}>
								{index + 1}. {item.label}
							</div>
						))
					) : (
						<div className={styles.listLine}>
							{NOT_AVAILABLE}
						</div>
					)}
				</div>

				<div className={styles.drawerSection}>
					<div className={styles.drawerSection__heading}>
						{t('learning.viewDetails.assignment')}
					</div>
					{scopes.length ? (
						scopes.map((scope) => (
							<div key={scope} className={styles.detailLine}>
								{t(
									`learning.viewDetails.scopes.${scope}`,
									{ defaultValue: scope }
								)}
							</div>
						))
					) : (
						<div className={styles.detailLine}>
							{NOT_AVAILABLE}
						</div>
					)}
				</div>

				<div className={styles.drawerSection}>
					<div className={styles.drawerSection__heading}>
						{t('learning.viewDetails.scheduling')}
					</div>
					<div className={styles.detailLine}>
						{t('learning.viewDetails.due')}:{' '}
						{assignment?.dueDate
							? formatCardDate(assignment.dueDate)
							: t('learning.viewDetails.none')}
					</div>
					<div className={styles.detailLine}>
						{t('learning.viewDetails.certificationExpiration')}:{' '}
						{certificationLabel(source) || NOT_AVAILABLE}
					</div>
				</div>

				<div className={styles.drawerSection}>
					<div className={styles.drawerSection__heading}>
						{t('learning.viewDetails.languages')}
					</div>
					<div className={styles.detailLine}>
						{languages.length
							? languages.join(', ')
							: NOT_AVAILABLE}
					</div>
				</div>
			</Drawer>
		);
	}
);

export default ViewDetailsDrawer;